/**
 * SwitchComponent - manages switch state and interaction logic.
 * Handles toggle, proximity, and button switch types with collision-based proximity detection.
 */

import { Actor } from '../../actors/Actor.js';
import { EngineClass } from '../../systems/ClassRegistry.js';
import { Delegate } from '../../utils/Delegate.js';
import { property } from '../../utils/serialization/decorator.js';

import { ProximityInteractableComponent, type ProximityInteractableOptions } from './ProximityInteractableComponent.js';

export enum SwitchType {
  /** Can be switched on/off repeatedly */
  Toggle = 'toggle',
  /** Stays activated while in proximity zone, deactivates when leaving */
  Proximity = 'proximity',
  /** Fires an event every time it's pressed, no state change */
  Button = 'button'
}

export enum SwitchState {
  /** Switch is off (for toggle) or inactive (for one-time) */
  Off = 'off',
  /** Switch is on (for toggle) or active (for one-time) */
  On = 'on'
}

export interface SwitchComponentOptions extends ProximityInteractableOptions {
  /** Type of switch behavior */
  switchType?: SwitchType;
  /** Initial state of the switch */
  initialState?: SwitchState;
}

export type SwitchEventDelegate = Delegate<[SwitchComponent], void>;

@EngineClass('SwitchComponent')
export class SwitchComponent extends ProximityInteractableComponent {
  @property()
  public switchType: SwitchType = SwitchType.Toggle;

  @property()
  public initialState: SwitchState = SwitchState.Off;

  // State management
  private currentState: SwitchState = SwitchState.Off;
  private interactionEnabled: boolean = true;

  // Event delegates
  public readonly onActivated: SwitchEventDelegate = new Delegate<[SwitchComponent], void>();
  public readonly onDeactivated: SwitchEventDelegate = new Delegate<[SwitchComponent], void>();
  public readonly onInteractionAttempt: SwitchEventDelegate = new Delegate<[SwitchComponent], void>();
  public readonly onPressed: SwitchEventDelegate = new Delegate<[SwitchComponent], void>();

  public override initialize(options?: SwitchComponentOptions): void {
    super.initialize(options);
    this.switchType = options?.switchType ?? this.switchType;
    this.initialState = options?.initialState ?? this.initialState;

    this.currentState = this.initialState;

    // Configure base class interaction mode
    // Toggle and Button types use key press interaction
    // Proximity type uses automatic activation
    this.useKeyPressInteraction = this.switchType !== SwitchType.Proximity;
  }

  public override postLoad(): void {
    super.postLoad();
    this.currentState = this.initialState;
  }

  /**
   * Activates the switch (turns it on)
   */
  public activate(): void {
    if (this.currentState === SwitchState.Off) {
      this.currentState = SwitchState.On;
      this.onActivated.invoke(this);
    }
  }

  /**
   * Deactivates the switch (turns it off)
   * Only works for Toggle switches
   */
  public deactivate(): void {
    if (this.currentState === SwitchState.On) {
      this.currentState = SwitchState.Off;
      this.onDeactivated.invoke(this);
    }
  }

  /**
   * Toggles the switch state
   */
  public toggle(): void {
    if (this.currentState === SwitchState.Off) {
      this.activate();
    } else {
      this.deactivate();
    }
  }

  /**
   * Checks if the switch is currently active (on)
   */
  public isActive(): boolean {
    return this.currentState === SwitchState.On;
  }

  /**
   * Gets the current switch state
   */
  public getState(): SwitchState {
    return this.currentState;
  }

  /**
   * Gets the switch type
   */
  public getSwitchType(): SwitchType {
    return this.switchType;
  }

  /**
   * Enables interactions with the switch
   */
  public enableInteraction(): void {
    this.interactionEnabled = true;
  }

  /**
   * Disables interactions with the switch
   */
  public disableInteraction(): void {
    this.interactionEnabled = false;
  }

  /**
   * Checks if interactions are currently enabled
   */
  public isInteractionEnabled(): boolean {
    return this.interactionEnabled;
  }

  // IInteractable implementation
  public override beginInteract(player: Actor): boolean {
    if (!this.canInteract(player)) return false;

    this.onInteractionAttempt.invoke(this);
    if (this.switchType === SwitchType.Button) {
      // Button type: fire press event every time, no state change
      this.onPressed.invoke(this);
    } else {
      // Toggle: use existing toggle logic
      this.toggle();
    }
    return true;
  }

  public override canInteract(player: Actor): boolean {
    // Check if interactions are enabled and player is in range
    if (!this.interactionEnabled || !this.isPlayerInRange(player)) {
      return false;
    }

    // Proximity switches don't respond to key presses - they're automatic
    if (this.switchType === SwitchType.Proximity) {
      return false;
    }

    return true;
  }

  public override getInteractionPrompt(): string {
    switch (this.switchType) {
      case SwitchType.Toggle:
        return this.isActive() ? 'Press E to Turn Off' : 'Press E to Turn On';
      case SwitchType.Button:
        return 'Press E to Activate';
      case SwitchType.Proximity:
        return '';
      default:
        return 'Press E to Interact';
    }
  }

  public override getInteractionPriority(): number {
    return 80; // Medium priority (lower than doors)
  }

  /**
   * Override to handle proximity-based auto-activation
   */
  protected override onPlayerEntered(player: Actor): void {
    // For Proximity mode: auto-activate when any player enters
    if (this.switchType === SwitchType.Proximity && !this.isActive()) {
      this.activate();
    }
  }

  /**
   * Override to handle proximity-based auto-deactivation
   */
  protected override onPlayerLeft(player: Actor): void {
    // For Proximity mode: auto-deactivate when player leaves
    if (this.switchType === SwitchType.Proximity && this.isActive()) {
      this.deactivate();
    }
  }

  //====================================================================================================
  // Editor Functions
  //====================================================================================================

  public override getEditorClassIcon(): string | null {
    return 'Icon_Switch';
  }
}
